import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import useUserDevice from '../../eyejack-web-core/src/ui/hooks/useUserDevice';
import { Spinner } from '../../eyejack-web-core/src/ui/components/Elements/Spinner';
import { XRLaunchButton } from '../Generics/XRLaunchButton';
import { CopyToClipboardTrigger } from '../../eyejack-web-core/src/ui/components/Generics/CopyToClipboardTrigger';
import { QrCodeGenerator } from '../../eyejack-web-core/src/ui/components/QrCodeGenerator';
import { useEJXLaunchUrl } from '../../eyejack-web-core/src/ui/hooks/useEJXLaunchUrl';
import Logo from '../../assets/logo.webp';
import DesktopBG from '../../assets/splash/bg-desktop.webp';
import MobileBG from '../../assets/splash/bg-mobile.webp';
import MobileBGElements from '../../assets/splash/bg-mobile-elements.webp';
import DesktopBGElements from '../../assets/splash/bg-desktop-elements.webp';
import useXRErrors from '../../eyejack-web-core/src/ui/hooks/useXRErrors';
import emitter from '../../eyejack-web-core/src/core/events/eventemitter';

type SplashProps = {
  contentLoaded: boolean;
};

export function Splash({ contentLoaded }: SplashProps) {
  const { device } = useUserDevice();
  const { xrPermissionStatus, xrSupportStatus, xrOtherStatus } = useXRErrors();

  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (xrOtherStatus === false) {
      setError('WebXR is not defined in this browser.');
    } else if (xrSupportStatus === false) {
      setError('WebXR is not supported on this device.');
    } else if (xrPermissionStatus === 'denied') {
      setError('Allow camera access in the app settings.');
    }
  }, [xrOtherStatus, xrSupportStatus, xrPermissionStatus]);

  const [pageType, setPageType] = useState<'loading' | 'default'>(contentLoaded ? 'default' : 'loading');

  const uiType = useMemo(() => {
    if (pageType === 'loading') {
      return 'loading';
    } else return 'default';
  }, [pageType]);

  useEffect(() => {
    const handleLoaded = () => {
      setPageType('default');
    };
    emitter.on('loaded', handleLoaded);
    return () => {
      emitter.off('loaded', handleLoaded);
    };
  }, []);

  const deviceType = useMemo(() => {
    if (device === undefined) {
      return 'loading';
    } else if (
      device === 'iOS' ||
      device === 'Android' ||
      device === 'AppClip'
    ) {
      return 'mobile';
    } else return 'desktop';
  }, [device]);

  return (
    <div
      className={clsx(
        'bg-cover bg-center bg-black h-full w-full flex text-white pointer-events-auto items-center xpx-5 xpy-12 ',
        {
          '': deviceType === 'desktop',
          'flex-col': deviceType !== 'desktop',
        }
      )}
      style={
        deviceType === 'desktop'
          ? {
              backgroundImage: `url(${DesktopBG})`,
            }
          : {
              backgroundImage: `url(${MobileBG})`,
            }
      }
    >
      {deviceType === 'desktop' && (
        // <div className="flex flex-col items-center justify-center w-full h-full p-[15%] min-w-[780px]">
        <DesktopSplash />
        // </div>
      )}
      {deviceType === 'mobile' && (
        <div
          className="animate-fade-in max-w-[500px] w-full bg-cover h-full"
          style={{
            backgroundImage: `url(${MobileBGElements})`,
          }}
        >
          <div className="animate-fade-in flex flex-col items-center justify-center flex-grow basis-1/2 xh-full w-full px-5 pt-36 pb-12">
            <img
              className={clsx('w-full h-auto max-w-[310px]', {
                '': uiType === 'loading',
              })}
              src={Logo}
            />
          </div>
        </div>
      )}
      {deviceType === 'loading' && (
        <div className="flex flex-col justify-center items-center px-10 pb-10 basis-1/2 h-full">
          <Spinner size="lg" />
        </div>
      )}
      {deviceType === 'mobile' && (
        <>
          <div
            className={clsx(
              'flex flex-col items-center flex-grow basis-1/2 px-5 pb-14 animate-fade-in',
              {
                'justify-end': uiType === 'default',
                'justify-center': uiType === 'loading',
              }
            )}
          >
            {uiType === 'loading' && (
              <div className="animate-fade-in pb-5 flex justify-center items-center flex-col">
                <Spinner size="lg" />
                <p className="pt-8 font-inter">Loading...</p>
              </div>
            )}

            {uiType === 'default' && (
              <>
                {error ? (
                  <>
                    {device === 'Android' && (
                      <ul className="px-10 font-secondary-sans flex flex-col gap-1 text-[22px] list-decimal pb-10 pt-10">
                        <li className="">
                          Tap the <span className="font-bold">lock icon</span>{' '}
                          next to the website address
                        </li>
                        <li>
                          Select <span className="font-bold">Permissions</span>
                        </li>
                        <li>
                          Enable{' '}
                          <span className="font-bold">Augmented Reality</span>
                        </li>
                      </ul>
                    )}
                    <h2 className="text-2xl font-secondary-sans items-end text-center animate-fade-in pb-12 px-5 leading-[31px]">
                      {error}
                    </h2>
                  </>
                ) : (
                  <>
                    <h2 className="text-lg md:text-xl items-end text-center animate-fade-in font-inter pb-12 px-5 leading-[115%]">
                      Access to your camera and microphone is required
                    </h2>
                  </>
                )}
              </>
            )}
            {uiType !== 'loading' && (
              <div className="">
                <XRLaunchButton />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DesktopSplash() {
  const url = useEJXLaunchUrl();
  return (
    <div
      className=" bg-cover bg-no-repeat bg-center flex flex-col 3xl:gap-14 md:gap-10 gap-8 h-full w-full items-center justify-center md:flex-wrap"
      style={{
        backgroundImage: `url(${DesktopBGElements})`,
      }}
    >
      <div className="flex-shrink 3xl:max-w-[415px] md:max-w-[320px] max-w-[210px] w-full h-auto">
        <img className="w-full h-auto" src={Logo} />
      </div>
      <div className="w-fit flex flex-col 3xl:gap-14 md:gap-10 gap-8 items-center justify-center">
        <QrCodeGenerator value={url} fgColor="#000000" size={250} />
        <h2 className="text-white text-center md:text-[22px] text-xl font-helvetica-light md:leading-[26px] leading-[22px] tracking-[-0.41px]">
          Scan the QR code to launch the
          <br />
          The Sea AR Experience.
        </h2>
        <CopyToClipboardTrigger text={url}>
          <div className="border-2 min-w-[250px] border-white border-opacity-30 py-3 px-4 max-w-xs flex gap-x-2 relative cursor-pointer hover:opacity-50 hover:border-opacity-50">
            <span className="link flex-grow text-center text-helvetica-heavy max-w-[310px] whitespace-nowrap overflow-hidden text-ellipsis w-full">
              COPY LINK
            </span>
          </div>
        </CopyToClipboardTrigger>
      </div>
    </div>
  );
}
